/**
 * Model Fit Explainer - Generate human-readable explanations for model recommendations
 */

import type { ModelScore } from '@/lib/modelScoring';
import type { WizardPersona, WizardEnvironment } from '@/stores/wizardStore';

export interface ExplainerInput {
  modelScore: ModelScore;
  personas: WizardPersona[];
  environment: WizardEnvironment;
  modelDetails: {
    display_name: string;
    params_b: number;
    isOnPrem: boolean;
  };
  topCloudScore?: number;
}

export interface ExplainerOutput {
  highlights: string[];
  considerations: string[];
  personaFit: string[];
}

export function explainModelFit(input: ExplainerInput): ExplainerOutput {
  const { modelScore, personas, environment, modelDetails, topCloudScore } = input;
  const { benchmarkBreakdown } = modelScore;
  
  const highlights: string[] = [];
  const considerations: string[] = [];
  const personaFit: string[] = [];

  // Extract benchmarks
  const humaneval = benchmarkBreakdown?.humaneval;
  const mmlu = benchmarkBreakdown?.mmlu;
  const gpqa = benchmarkBreakdown?.gpqa;
  const arenaElo = benchmarkBreakdown?.arena_elo;

  // Get persona expertise types
  const expertiseTypes = personas.map(p => p.expertiseType);
  const hasCoding = expertiseTypes.includes('coding');
  const hasThinking = expertiseTypes.includes('thinking');
  const hasLongConversation = expertiseTypes.includes('long-conversation');
  const hasSummarization = expertiseTypes.includes('summarization');

  // === PERSONA-BENCHMARK MATCHING ===
  
  // Coding + HumanEval
  if (hasCoding && humaneval !== undefined) {
    if (humaneval >= 85) {
      highlights.push(`Elite coding capability (${humaneval.toFixed(1)}% HumanEval) matches your coding-focused personas.`);
      personaFit.push('🎯 Optimized for developer workflows and code generation tasks.');
    } else if (humaneval >= 70) {
      highlights.push(`Strong code generation (${humaneval.toFixed(1)}% HumanEval) suitable for developer workflows.`);
    }
  }

  // Thinking + GPQA
  if (hasThinking && gpqa !== undefined) {
    if (gpqa >= 50) {
      highlights.push(`Elite graduate-level reasoning (${gpqa.toFixed(1)}% GPQA) excels at complex analytical tasks.`);
      personaFit.push('🎯 Ideal for data analysis, research, and complex problem-solving.');
    } else if (gpqa >= 40) {
      highlights.push(`Strong reasoning capability (${gpqa.toFixed(1)}% GPQA) handles sophisticated problem-solving.`);
    }
  }

  // Long-conversation or Summarization + Arena ELO
  if ((hasLongConversation || hasSummarization) && arenaElo !== undefined) {
    if (arenaElo >= 1250) {
      highlights.push(`Top-tier conversational ability (${arenaElo} Arena ELO) ideal for extended interactions.`);
      personaFit.push('🎯 Excels at multi-turn conversations and context retention.');
    } else if (arenaElo >= 1200) {
      highlights.push(`High general capability (${arenaElo} Arena ELO) performs well across diverse tasks.`);
    }
  }

  // MMLU reasoning strength (general)
  if (mmlu !== undefined) {
    if (mmlu >= 85) {
      highlights.push(`Exceptional broad knowledge (${mmlu.toFixed(1)}% MMLU) across diverse domains.`);
    } else if (mmlu >= 75) {
      highlights.push(`Strong general reasoning (${mmlu.toFixed(1)}% MMLU) handles complex queries effectively.`);
    }
  }

  // === ON-PREM COMPETITIVE ANALYSIS ===
  if (modelDetails.isOnPrem && topCloudScore !== undefined) {
    const scoreDiff = ((modelScore.score / topCloudScore) * 100) - 100;
    const absDiff = Math.abs(scoreDiff);

    if (absDiff <= 2) {
      highlights.push('Matches GPT-4o performance (within 2%) while running on-premises with full data control.');
      personaFit.push('💰 Eliminates cloud API costs while maintaining elite performance.');
    } else if (absDiff <= 5) {
      highlights.push('Competitive with GPT-4o (within 5%) while maintaining on-premises deployment benefits.');
      personaFit.push('💰 Significantly lower operational costs with near-cloud performance.');
    } else if (absDiff <= 10) {
      highlights.push('Near-GPT-4o performance (within 10%) with significantly lower operational costs.');
    }
  }

  // === ENVIRONMENT CONSTRAINT ANALYSIS ===
  
  // Latency constraints
  if (environment.latencyTolerance === 'snappy') {
    if (modelDetails.params_b > 70) {
      considerations.push(`⚠️ Large model size (${modelDetails.params_b}B parameters) may impact sub-second latency targets.`);
      considerations.push('💡 Consider smaller variants or optimize inference for latency-critical workloads.');
    } else if (modelDetails.params_b <= 13) {
      highlights.push(`Lightweight model (${modelDetails.params_b}B parameters) optimized for fast response times.`);
    }
  } else if (environment.latencyTolerance === 'batch' && modelDetails.params_b >= 70) {
    highlights.push(`Large model (${modelDetails.params_b}B parameters) leverages batch processing for maximum quality.`);
  }

  // Concurrency considerations
  if (environment.concurrentUsers > 5000 && modelDetails.params_b <= 13) {
    highlights.push(`Efficient model scales well for high concurrent user loads (${environment.concurrentUsers.toLocaleString()}).`);
  }

  // === MULTI-PERSONA INTELLIGENCE ===
  if (personas.length > 1) {
    const expertiseCounts = expertiseTypes.reduce((acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const primaryExpertise = Object.entries(expertiseCounts).sort(([, a], [, b]) => b - a)[0];
    const secondaryExpertise = Object.entries(expertiseCounts).sort(([, a], [, b]) => b - a)[1];

    if (primaryExpertise && secondaryExpertise) {
      const primaryWeight = Math.round((primaryExpertise[1] / personas.length) * 100);
      personaFit.push(
        `Optimized for ${formatExpertiseType(primaryExpertise[0])} tasks (${primaryWeight}% weighting) with strong ${formatExpertiseType(secondaryExpertise[0])} support.`
      );
    }
  }

  // Limit to most relevant insights (max 4-5 per section)
  return {
    highlights: highlights.slice(0, 4),
    considerations: considerations.slice(0, 3),
    personaFit: personaFit.slice(0, 3),
  };
}

function formatExpertiseType(type: string): string {
  const formatted: Record<string, string> = {
    'coding': 'coding',
    'thinking': 'reasoning',
    'long-conversation': 'conversational',
    'summarization': 'summarization',
    'general': 'general-purpose',
  };
  return formatted[type] || type;
}
